import React, { useEffect, useState } from 'react';
import { AlertCircle, Key, Loader2, Lock, Save, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { BACKEND_URL } from '../../config';

const AdminCredentialsSection = () => {
  // Admin credentials state
  const [adminCredentials, setAdminCredentials] = useState({ email: '', configured: false });
  const [loadingAdminCreds, setLoadingAdminCreds] = useState(true);
  const [updatingAdminCreds, setUpdatingAdminCreds] = useState(false);
  const [adminCredsForm, setAdminCredsForm] = useState({
    currentPassword: '',
    newEmail: '',
    newPassword: '',
    confirmPassword: '',
  });

  // reCAPTCHA config state
  const [recaptchaConfig, setRecaptchaConfig] = useState({ enabled: true, score_threshold: 0.5 });
  const [loadingRecaptchaConfig, setLoadingRecaptchaConfig] = useState(true);
  const [updatingRecaptchaConfig, setUpdatingRecaptchaConfig] = useState(false);

  const fetchAdminCredentials = async () => {
    setLoadingAdminCreds(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-credentials`);
      const data = await response.json();
      setAdminCredentials(data);
    } catch (error) {
      console.error('Error fetching admin credentials:', error);
      toast.error('Failed to load admin credentials');
    } finally {
      setLoadingAdminCreds(false);
    }
  };

  const handleUpdateAdminCredentials = async () => {
    if (!adminCredsForm.currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (adminCredsForm.newPassword && adminCredsForm.newPassword !== adminCredsForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (!adminCredsForm.newEmail && !adminCredsForm.newPassword) {
      toast.error('Please enter a new email or password to update');
      return;
    }
    setUpdatingAdminCreds(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin-credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: adminCredsForm.currentPassword,
          new_email: adminCredsForm.newEmail || undefined,
          new_password: adminCredsForm.newPassword || undefined,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Admin credentials updated successfully');
        setAdminCredsForm({ currentPassword: '', newEmail: '', newPassword: '', confirmPassword: '' });
        fetchAdminCredentials();
      } else {
        toast.error(data.detail || 'Failed to update credentials');
      }
    } catch (error) {
      console.error('Error updating admin credentials:', error);
      toast.error('Failed to update admin credentials');
    } finally {
      setUpdatingAdminCreds(false);
    }
  };

  const fetchRecaptchaConfig = async () => {
    setLoadingRecaptchaConfig(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/recaptcha-config`);
      const data = await response.json();
      setRecaptchaConfig({
        enabled: data.enabled ?? true,
        score_threshold: data.score_threshold ?? 0.5,
      });
    } catch (error) {
      console.error('Error fetching reCAPTCHA config:', error);
      toast.error('Failed to load reCAPTCHA configuration');
    } finally {
      setLoadingRecaptchaConfig(false);
    }
  };

  const handleUpdateRecaptchaConfig = async () => {
    setUpdatingRecaptchaConfig(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/recaptcha-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recaptchaConfig),
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('reCAPTCHA configuration updated successfully');
        fetchRecaptchaConfig();
      } else {
        toast.error(data.detail || 'Failed to update reCAPTCHA configuration');
      }
    } catch (error) {
      console.error('Error updating reCAPTCHA config:', error);
      toast.error('Failed to update reCAPTCHA configuration');
    } finally {
      setUpdatingRecaptchaConfig(false);
    }
  };

  useEffect(() => {
    fetchAdminCredentials();
    fetchRecaptchaConfig();
  }, []);

  return (
    <div className="space-y-6">
      {/* Admin Credentials Card */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-red-600 to-pink-600">
          <CardTitle className="text-white flex items-center gap-2">
            <Key className="w-5 h-5" />
            Admin Credentials Management
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {loadingAdminCreds ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-red-600" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-600 mb-1">Current Admin Email:</p>
                <p className="text-lg font-semibold text-slate-800">{adminCredentials.email}</p>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Current Password *</label>
                  <input
                    type="password"
                    value={adminCredsForm.currentPassword}
                    onChange={(e) => setAdminCredsForm((prev) => ({ ...prev, currentPassword: e.target.value }))}
                    placeholder="Enter current password"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">New Email (Optional)</label>
                  <input
                    type="email"
                    value={adminCredsForm.newEmail}
                    onChange={(e) => setAdminCredsForm((prev) => ({ ...prev, newEmail: e.target.value }))}
                    placeholder="Leave blank to keep current"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">New Password (Optional)</label>
                  <input
                    type="password"
                    value={adminCredsForm.newPassword}
                    onChange={(e) => setAdminCredsForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                    placeholder="Leave blank to keep current"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>

                {adminCredsForm.newPassword && (
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Confirm New Password</label>
                    <input
                      type="password"
                      value={adminCredsForm.confirmPassword}
                      onChange={(e) => setAdminCredsForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                      placeholder="Re-enter new password"
                      className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>
                )}

                <button
                  onClick={handleUpdateAdminCredentials}
                  disabled={updatingAdminCreds}
                  className="w-full bg-red-600 hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {updatingAdminCreds ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Updating...</>
                  ) : (
                    <><Save className="w-4 h-4" /> Update Credentials</>
                  )}
                </button>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Security Requirements
                </h4>
                <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
                  <li>Password must be at least 8 characters long</li>
                  <li>Must contain uppercase, lowercase, and number</li>
                  <li>Current password is required to make changes</li>
                  <li>Default: admin@pmskart.com / @one@tree@hill</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* reCAPTCHA Configuration Card */}
      <Card className="overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600">
          <CardTitle className="text-white flex items-center gap-2">
            <Shield className="w-5 h-5" />
            reCAPTCHA Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {loadingRecaptchaConfig ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <label className="text-sm font-semibold text-slate-700">Enable reCAPTCHA Protection</label>
                  <p className="text-xs text-slate-500 mt-1">Protect contact forms from spam and abuse</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={recaptchaConfig.enabled}
                      onChange={(e) => setRecaptchaConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                  <span className="text-sm font-medium text-slate-700">
                    {recaptchaConfig.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Score Threshold (v3)</label>
                <p className="text-xs text-slate-500 mb-2">
                  reCAPTCHA v3 returns a score (1.0 is very likely a good interaction, 0.0 is very likely a bot). Set the minimum acceptable score.
                </p>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={recaptchaConfig.score_threshold}
                    onChange={(e) => setRecaptchaConfig((prev) => ({ ...prev, score_threshold: parseFloat(e.target.value) }))}
                    className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                  />
                  <span className="text-lg font-bold text-purple-600 min-w-[3rem] text-right">
                    {recaptchaConfig.score_threshold.toFixed(1)}
                  </span>
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>0.0 (Bot)</span>
                  <span>0.5 (Recommended)</span>
                  <span>1.0 (Human)</span>
                </div>
              </div>

              <button
                onClick={handleUpdateRecaptchaConfig}
                disabled={updatingRecaptchaConfig}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {updatingRecaptchaConfig ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4" /> Save reCAPTCHA Settings</>
                )}
              </button>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  About reCAPTCHA v3
                </h4>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                  <li>Works invisibly in the background - no user interaction needed</li>
                  <li>Returns a score from 0.0 (likely bot) to 1.0 (likely human)</li>
                  <li>Recommended threshold: 0.5 (balanced protection)</li>
                  <li>Lower threshold = more restrictive, may block legitimate users</li>
                  <li>Higher threshold = more permissive, may allow more spam</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCredentialsSection;
